'use client'

// Opts the grid out of React Compiler. Its parts read state through builder
// calls on rows, columns and headers that keep their identity across renders
// (row.getIsExpanded(), column.getIsResizing()); compiled, those reads are
// cached against the unchanged object and never run again. The grid memoizes
// by hand instead, and the component that renders it still compiles.
'use no memo'

// Ours, listed for the next sync with ReUI (definitions also carry an `Ours:` comment):
// - `DataGridFeatures` is our `TableFeatures`, and the column meta lives in `data-grid-column-meta.ts`.
// - ReUI's two `setOptions` effects and `columnsResizeMode` are gone: `TableStateProvider` owns the
//   table options.
// - Props `fillColumnId`, `getCellClassName`, `getRowProps`, `onColumnSizingPreview`,
//   `renderCellContent`, `renderHeadCellContent` and `stickySides`, and the `bodyCell`, `headCell`
//   and `resizeHandle` keys of `tableClassNames`.
// - `getColumnHeaderLabel` falls back to `meta.label`; cell DOM ids use a collision-free encoding.
// - The two `any` defaults carry `oxlint-disable` directives with reasons.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'
import type { ReactNode } from 'react'
import {
  type DataGridI18nConfig,
  type DataGridI18nOverrides,
  mergeDataGridI18n,
} from './data-grid-i18n'
import type { TableFeatures as PlatformTableFeatures } from '../table/tanstack-table'
import type {
  Cell,
  Column,
  ColumnFiltersState,
  Header,
  ReactTable,
  RowData,
  SortingState,
  Table,
  TableFeatures,
} from '@tanstack/react-table'

import { cn } from '@repo/ui/lib/utils'

import type { DataGridColumnCellEdit, DataGridColumnMeta } from './data-grid-column-meta'

export type { DataGridColumnCellEdit, DataGridColumnMeta }

// The grid is typed against our own feature set rather than ReUI's runtime `dataGridFeatures`
// bundle, so its parts accept our rows and columns and importing it pulls in no row models.
export type DataGridFeatures = PlatformTableFeatures

/**
 * The grid's internal view of the table.
 *
 * `TFeatures` is invariant in v9 and an unresolved generic one collapses to a
 * union that includes the bare core arm, so no generic signature can call
 * `getVisibleCells()`, `getStartVisibleLeafColumns()` and friends. The public
 * components stay generic so consumers can pass any bundle they like; the
 * table is widened to this concrete type exactly once, on the way into
 * context, and every internal component reads it from there.
 */
export type DataGridTableInstance<TData extends object> = ReactTable<DataGridFeatures, TData>

/** Label for headers / column visibility: `meta.headerTitle`, string `columnDef.header`, or `column.id`. */
export function getColumnHeaderLabel<TData extends RowData, TValue>(
  column: Column<DataGridFeatures, TData, TValue>,
): string {
  const meta = column.columnDef.meta as undefined | { headerTitle?: string; label?: string }
  if (typeof meta?.headerTitle === 'string') return meta.headerTitle
  // Ours: our columns name themselves with `meta.label`, so it stands in for `headerTitle`.
  if (typeof meta?.label === 'string') return meta.label
  const defHeader = column.columnDef.header
  if (typeof defHeader === 'string') return defHeader
  return String(column.id)
}

/**
 * The td contract for spreadsheet selection. Call inside a
 * `Subscribe source={table.atoms.cellSelection}` render only: the reads are
 * builder calls whose state dependency React Compiler cannot see, exactly the
 * trap the row-selection checkbox documents.
 */
export function getDataGridCellSelectionCellAttrs<TData extends object>(
  cell: Cell<DataGridFeatures, TData, unknown>,
): {
  'aria-selected': boolean
  'data-cell-edge-bottom': true | undefined
  'data-cell-edge-left': true | undefined
  'data-cell-edge-right': true | undefined
  'data-cell-edge-top': true | undefined
  'data-cell-focused': true | undefined
  'data-cell-selected': true | undefined
  'data-col-id': string
} {
  const selected = cell.getIsSelected()
  const edges = cell.getSelectionEdges()
  return {
    'aria-selected': selected,
    'data-cell-edge-bottom': edges.bottom || undefined,
    'data-cell-edge-left': edges.left || undefined,
    'data-cell-edge-right': edges.right || undefined,
    'data-cell-edge-top': edges.top || undefined,
    'data-cell-focused': cell.getIsFocused() || undefined,
    'data-cell-selected': selected || undefined,
    'data-col-id': cell.column.id,
  }
}

/**
 * meta.headerColSpan: the sibling leaves each owning leaf's header covers,
 * by owner id. Each must render right after the last in its pin family, or
 * the span ends there; the header rows, the resize indicator and the move
 * and drop targets all read spans from here.
 */
export function getDataGridHeaderSpans<TData extends object>(table: DataGridTableInstance<TData>) {
  const spans = new Map<string, Column<DataGridFeatures, TData, unknown>[]>()
  const families = [
    table.getStartVisibleLeafColumns(),
    table.getCenterVisibleLeafColumns(),
    table.getEndVisibleLeafColumns(),
  ] as Column<DataGridFeatures, TData, unknown>[][]

  for (const family of families) {
    for (let index = 0; index < family.length; index++) {
      const owner = family[index]!
      const span = Math.floor(owner.columnDef.meta?.headerColSpan ?? 1)
      if (span < 2) continue

      // The next span - 1 siblings in definition order, so a span never
      // leaves its parent group; a hidden one is skipped, not a gap.
      const siblings = owner.parent?.columns ?? table.getAllColumns()
      const at = siblings.findIndex(sibling => sibling.id === owner.id)
      const intended =
        at < 0 ? [] : siblings.slice(at + 1, at + span).filter(sibling => sibling.getIsVisible())
      const covered: Column<DataGridFeatures, TData, unknown>[] = []
      for (const sibling of intended) {
        const next = family[index + 1 + covered.length]
        if (next?.id !== sibling.id) break
        covered.push(next)
      }
      if (!covered.length) continue

      spans.set(owner.id, covered)
      index += covered.length
    }
  }

  return spans
}

/**
 * Selection chrome for a body td, activated by the data attributes above plus
 * the imperatively toggled `data-cell-fill-target` (fill-drag preview, written
 * outside React like the resize indicator). A ::before overlay, not outline
 * or box-shadow: an outline ring hugs the cell box and cannot line up with
 * the gridlines the perimeter paints on, and the pinned-column dividers
 * already own the cell's shadow slot (tailwind-merge would collapse a second
 * shadow-[...] into it).
 */
export const dataGridCellSelectionCellClasses = cn(
  'relative select-none',
  // A light tint: the selection must read as a range without drowning the
  // gridlines under a heavy fill. The focused cell stays unfilled. Pinned
  // cells must stay OPAQUE (they hide scrolled content), so they get the
  // tint pre-mixed over the background, and the focused pinned cell keeps
  // a solid background instead of turning transparent.
  'data-[cell-selected]:bg-primary/4',
  'data-[cell-selected]:data-pinned:bg-[color-mix(in_oklab,var(--primary)_4%,var(--data-grid-surface,var(--data-grid-card-surface,var(--background))))]',
  'data-[cell-focused]:bg-transparent!',
  'data-[cell-focused]:data-pinned:bg-[color:var(--data-grid-surface,var(--data-grid-card-surface,var(--background)))]!',
  // Every selection line is one layout-free ::before overlay per cell. It
  // reaches 1px BEYOND the cell so each line paints exactly ON the shared
  // gridline it replaces: the range perimeter and the interior dividers
  // share this geometry, so where they meet they coincide instead of
  // stacking into a wider edge, every side stays exactly 1px, and
  // selecting never adds real borders that shift layout. At the table's
  // own boundary there is no shared gridline and the overshoot leaves the
  // scrollable content box, where it is CLIPPED - the perimeter simply
  // vanished on the first column, last column and last row. So every
  // overshoot that can hit a boundary goes through a variable the
  // boundary cells zero out. Longhand inset utilities only, logical
  // start/end
  // so RTL mirrors for free; a separate override rule cannot do this,
  // equal-specificity variants leave shorthand-vs-longhand to sort order.
  "data-[cell-selected]:before:pointer-events-none data-[cell-selected]:before:absolute data-[cell-selected]:before:start-[var(--data-grid-overlay-start,-1px)] data-[cell-selected]:before:end-[var(--data-grid-overlay-end,-1px)] data-[cell-selected]:before:top-[var(--data-grid-overlay-top,-1px)] data-[cell-selected]:before:bottom-[var(--data-grid-overlay-bottom,-1px)] data-[cell-selected]:before:border-primary data-[cell-selected]:before:content-['']",
  "data-[cell-focused]:before:pointer-events-none data-[cell-focused]:before:absolute data-[cell-focused]:before:start-[var(--data-grid-overlay-start,-1px)] data-[cell-focused]:before:end-[var(--data-grid-overlay-end,-1px)] data-[cell-focused]:before:top-[var(--data-grid-overlay-top,-1px)] data-[cell-focused]:before:bottom-[var(--data-grid-overlay-bottom,-1px)] data-[cell-focused]:before:border-primary data-[cell-focused]:before:content-['']",
  // Interior dividers: selected cells repaint their own end and bottom
  // gridlines on the overlay, so gridline-less grids still divide a range.
  // The gray color is guarded by not- variants (higher specificity), so a
  // side that belongs to the range perimeter deterministically stays
  // primary.
  'data-[cell-selected]:before:border-e data-[cell-selected]:before:border-b',
  'data-[cell-selected]:not-data-[cell-edge-right]:before:border-e-border',
  'data-[cell-selected]:not-data-[cell-edge-bottom]:before:border-b-border',
  // The Sheets model: a single selected cell IS its own range, so the four
  // edge attributes below already draw its full primary box; inside a
  // larger range the anchor reads by its unfilled background alone. The
  // explicit ring only covers a focused cell with no selection at all.
  // Edge sides are logical (border-e/border-s): the feature derives edges
  // from display-order column indexes, which mirror in RTL.
  'data-[cell-focused]:not-data-[cell-selected]:before:border',
  'data-[cell-edge-bottom]:before:border-b data-[cell-edge-left]:before:border-s data-[cell-edge-right]:before:border-e data-[cell-edge-top]:before:border-t',
  // While a fill drag is live the preview border is the ONE painter for
  // the whole pending region, so the source cells' own chrome rests -
  // otherwise its lines double against the region border at every shared
  // edge. display is the no-cascade-fight switch (see below).
  'in-data-[cell-filling]:data-[cell-selected]:before:hidden',
  'in-data-[cell-filling]:data-[cell-focused]:before:hidden',
  // While the built-in editor is open its outline is the one border; the
  // edited cell's own overlay chrome hides so nothing peeks around the
  // flush editor. display is the switch because no other before: rule
  // sets it, so there is no cascade order to lose.
  'in-data-[cell-editing]:data-[cell-focused]:before:hidden',
  // Clamped to the cell edge at the grid's own boundary, the overlay's
  // lines sit flush against the container border instead of being clipped
  // away outside the scrollable content. A custom property has no cascade
  // fight to lose. First/last-child cover the boundary columns: when a
  // filler td follows the last data column, that cell's overshoot lands
  // INSIDE the table and needs no clamp, and the td:first/last-child of a
  // row are its logical start/end in RTL too.
  'in-[tr:last-child]:[--data-grid-overlay-bottom:0px]',
  // The top overshoot lands under a sticky header, which is positioned and
  // paints over it - so the first row's perimeter lost its top line. Clamp
  // inside the cell there, exactly like the other three boundaries.
  'in-[tr:first-child]:[--data-grid-overlay-top:0px]',
  'first:[--data-grid-overlay-start:0px]',
  'last:[--data-grid-overlay-end:0px]',
  // A pinned column is a boundary like the grid's own edges: the sticky
  // neighbor would cover an on-gridline overlay line, and re-painting it
  // from the pinned side doubles into a 2px blur whenever subpixel
  // positions disagree (browser zoom, fractional fill widths). One
  // painter instead: the overlay clamps inside its own cell there, the
  // same custom-property clamp the first and last cells use.
  '[&:has(+td[data-pinned])]:[--data-grid-overlay-end:0px]',
  '[&:has(+[data-slot=data-grid-table-fill-body-cell]:last-child)]:[--data-grid-overlay-end:0px]',
  '[&:has(+[data-slot=data-grid-table-fill-body-cell]+td[data-pinned])]:[--data-grid-overlay-end:0px]',
  '[td[data-pinned]+&]:[--data-grid-overlay-start:0px]',
  // Tint only: the dashed region outline is one overlay element drawn by
  // the fill session, so neighboring target cells never double their edges.
  'data-[cell-fill-target]:bg-primary/4',
  'data-[cell-fill-target]:data-pinned:bg-[color-mix(in_oklab,var(--primary)_4%,var(--data-grid-surface,var(--data-grid-card-surface,var(--background))))]',
)

export type DataGridApiFetchParams = {
  filters?: ColumnFiltersState
  pageIndex: number
  pageSize: number
  searchQuery?: string
  sorting?: SortingState
}

export type DataGridApiResponse<T> = {
  data: T[]
  empty: boolean
  pagination: {
    page: number
    total: number
  }
}

export type DataGridAutoSizeController = {
  /**
   * Records a width the caller commits for the armed column, so the next
   * measurement reads it as this coordinator's own write and keeps
   * reflowing, rather than as a user drag that stands it down.
   */
  adopt: (columnId: string, size: number) => void
  /**
   * Feeds the controller one viewport measurement: the SIGNED free space
   * between the scroll container and the table, negative while the table
   * overflows. The first sight of a `meta.autoSize` column absorbs the
   * free space into its width; after that, every measurement REFLOWS the
   * same growth - a wider window widens the column, a narrower one gives
   * the space back down to the column's `minSize` (or its starting width
   * when none is set) - so the grid tracks its container live. Commits
   * are coalesced for performance: sub-pixel deltas are dropped, a
   * streaming resize gets one leading commit plus one trailing commit at
   * settle, and a width the user dragged is never touched. Safe to call
   * from every viewport measurement; returns true when a sizing update
   * was dispatched.
   */
  apply: (freeSpace: number) => boolean
  /**
   * The width the armed column would reflow to at this free space, and the
   * width it holds now, without committing anything. Null when no column is
   * armed. A live (onChange) resize previews with it on every move.
   */
  preview: (freeSpace: number) => null | { columnId: string; current: number; size: number }
}

export interface DataGridContextProps<TData extends object> {
  /**
   * Internal coordinator for `meta.autoSize` columns. Lives at the core level
   * so every table variant and viewport instance shares one application state.
   */
  autoSize?: DataGridAutoSizeController
  /** Stable per-grid prefix for the DOM ids ARIA relations point at. */
  gridId: string
  /** The merged label set; components read `i18n.labels.<key>`. */
  i18n: DataGridI18nConfig
  isLoading: boolean
  props: DataGridLayoutProps<TData>
  recordCount: number
  table: DataGridTableInstance<TData>
}

/**
 * Everything `<DataGrid>` accepts except the two props the provider consumes
 * itself. Kept feature-agnostic: layout and messaging never depend on which
 * TanStack features the consumer registered.
 */
export type DataGridLayoutProps<TData extends object> = Omit<
  DataGridProps<TableFeatures, TData>,
  'children' | 'table'
>

type DataGridAutoSizeReflowState = {
  applied: null | { base: number; columnId: string; grown: number }
  lastCommitAt: number
  pendingFreeSpace: number
  settleTimer: null | ReturnType<typeof setTimeout>
}

/**
 * Ours: the DOM id of a body cell. `toDataGridDomId` never emits `-`, so the separators keep the
 * row and column apart: row `a-b` with column `c` and row `a` with column `b-c` differ.
 */
export function getDataGridCellDomId(gridId: string, rowId: string, columnId: string): string {
  return `${gridId}c-${toDataGridDomId(rowId)}-${toDataGridDomId(columnId)}`
}

/**
 * Row and column ids are consumer strings; DOM ids cannot carry spaces or
 * quotes. One shared sanitizer so `aria-activedescendant` writers and the id
 * renderers always agree on the same encoding.
 */
export function toDataGridDomId(value: string): string {
  // Ours: every character outside [A-Za-z0-9] becomes `_<hex code point>_`, so no two values
  // share an id (`a.b` and `a_b` used to) and the result never holds a `-`.
  return value.replace(/[^a-zA-Z0-9]/gu, char => `_${char.codePointAt(0)?.toString(16)}_`)
}

/**
 * Bookkeeping keyed on the TABLE STORE, never on a React instance: dev
 * StrictMode double-mounts the tree, so two controller instances serve one
 * page and only the first would remember what it absorbed - the second
 * reads the grown width as a user drag and freezes the column. The store
 * object is the one identity that provably survives that dance (the
 * absorbed sizing rides inside it), and the WeakMap lets a discarded
 * table take its bookkeeping with it.
 */
const dataGridAutoSizeStates = new WeakMap<object, DataGridAutoSizeReflowState>()

export interface DataGridCellChange<TData> {
  columnId: string
  /** Accessor value at dispatch time; enables consumer-side undo stacks. */
  previousValue: unknown
  /** The consumer's row object, so immutable write-back needs no second lookup. */
  row: TData
  rowId: string
  /** Typed value produced by `meta.cellEdit.parse`, or `clearValue` for clear/cut. */
  value: unknown
}

/**
 * Asks the consumer to open its editor for the focused cell: Enter or F2, or
 * a typed printable character (then `initialText` carries it, the
 * Notion/Airtable type-to-replace flow). Dispatched only for cells whose
 * column is writable via `meta.cellEdit`.
 */
export interface DataGridCellEditRequest<TData> {
  columnId: string
  /** The typed character that started the edit; absent for Enter and F2. */
  initialText?: string
  previousValue: unknown
  row: TData
  rowId: string
}

export interface DataGridCellRejection {
  columnId: string
  raw: string
  reason: 'invalid' | 'readonly'
  rowId: string
}

/**
 * One batch per operation, however many cells it spans: a 10k-cell paste is
 * one callback, one consumer setState, one table rebuild. The grid never
 * writes data itself.
 */
export interface DataGridCellsChangeDetails<TData> {
  changes: DataGridCellChange<TData>[]
  rejected: DataGridCellRejection[]
  source: DataGridCellsChangeSource
}

/** Which spreadsheet operation produced a cell change batch. */
export type DataGridCellsChangeSource = 'clear' | 'cut' | 'edit' | 'fill' | 'paste'

/** One resolved positive region, in pre-paginated display indexes. */
export interface DataGridCellSelectionBound {
  maxColumnIndex: number
  maxRowIndex: number
  minColumnIndex: number
  minRowIndex: number
}

/** What onCellSelectionChange receives, resolved from the live selection. */
export interface DataGridCellSelectionSnapshot {
  /** The most recent region, the one copy and paste act on. */
  activeBound: DataGridCellSelectionBound | null
  /** Every positive region, geometric order. */
  bounds: DataGridCellSelectionBound[]
  /** The virtually focused cell, the active range's anchor. Null when clear. */
  focused: null | { columnId: string; rowId: string }
  /**
   * Cells of the selection the view can show - the count that matches the
   * painted range and what a batch will touch.
   */
  visibleCellCount: number
}

/**
 * Lifecycle of a cell for the optional CRUD indications: a corner mark in
 * the amber tone for "dirty", the destructive tone for "invalid".
 */
export type DataGridCellStatus = 'dirty' | 'invalid'

/** What onCellsCopy receives after the grid writes the clipboard. */
export interface DataGridCopyDetails {
  /** True when the write came from a cut, which clears the region next. */
  cut: boolean
  /** The same content as a row-major grid of formatted fields. */
  grid: string[][]
  /** The TSV text that reached the clipboard. */
  text: string
}

export interface DataGridProps<TFeatures extends TableFeatures, TData extends object> {
  allRowsLoadedMessage?: ReactNode | string
  /**
   * Rendered inside the table body after the data rows: the slot a DRAFT
   * row lives in while it is being created, before it joins `data` and the
   * pagination math. Supply a `<tr>` of cells; the colgroup sizes them.
   */
  appendRow?: ReactNode
  children?: ReactNode
  className?: string
  emptyMessage?: ReactNode | string
  fetchingMoreMessage?: ReactNode | string
  /**
   * Ours: the column that takes the free width, as a `meta.fillWidth` column does, when it's
   * visible; while it's hidden, the columns share the free width. The grid's consumer picks it,
   * where `meta.fillWidth` is set by the column itself.
   */
  fillColumnId?: string
  /** Ours: extra classes for a body cell in every row renderer, such as its alignment. */
  getCellClassName?: (cell: Cell<DataGridFeatures, TData, unknown>) => string | undefined
  /**
   * Optional CRUD indication per cell: the classic corner mark on cells the
   * consumer tracks as edited or invalid.
   */
  getCellStatus?: (row: TData, columnId: string) => DataGridCellStatus | undefined
  /** Ours: per-row class, highlight and hover handlers, read by every body row (standard and virtual). */
  getRowProps?: (row: TData) => DataGridRowProps | undefined
  /**
   * Optional CRUD indication per row. Return undefined for no indication;
   * omit the prop entirely to render none anywhere.
   */
  getRowStatus?: (row: TData) => DataGridRowStatus | undefined
  /**
   * Overrides for every built-in string: menu items, aria labels,
   * pagination copy, state messages. Merged over the defaults; a more
   * specific component prop (`rowCreateLabel`, `loadingMessage`, the
   * pagination label props) still wins over its `i18n` counterpart.
   */
  i18n?: DataGridI18nOverrides
  isLoading?: boolean
  loadingMessage?: ReactNode | string
  loadingMode?: 'skeleton' | 'spinner'
  /**
   * Opens the consumer's editor from the keyboard: Enter, F2, or typing on
   * the focused cell. Absent: Enter keeps moving down, typing does nothing.
   */
  onCellEditRequest?: (request: DataGridCellEditRequest<TData>) => void
  /**
   * Receives every spreadsheet write batch (paste, cut, clear, fill, edit).
   * Served through the props getter like `onRowClick`, so an inline identity
   * never republishes the context. Absent: copy still works, writes no-op.
   */
  onCellsChange?: (details: DataGridCellsChangeDetails<TData>) => void
  /**
   * Fires after the grid writes the clipboard (Cmd/Ctrl+C or X, or the
   * native copy and cut events), e.g. to confirm with a toast.
   */
  onCellsCopy?: (details: DataGridCopyDetails) => void
  /**
   * Fires on every cell-selection change (focus moves, ranges grow or
   * clear) with a resolved snapshot, so a selection count, formula bar or
   * context panel needs no reach into TanStack internals.
   */
  onCellSelectionChange?: (snapshot: DataGridCellSelectionSnapshot) => void
  /**
   * Ours: the live widths of the columns a resize drag moves, on every move, then null once the
   * drag commits. A live drag writes only CSS variables, so a policy that reads widths (the phone
   * sticky budget) follows the drag through this.
   */
  onColumnSizingPreview?: (sizing: null | Record<string, number>) => void
  onRowClick?: (row: TData) => void
  /**
   * Renders an "Add row" affordance as the table body's last row; clicking
   * it is the consumer's cue to start creating a row. Absent: no row.
   */
  onRowCreate?: () => void
  recordCount: number
  /** Ours: replaces the body cell content in every row renderer, e.g. to wrap it in a link. */
  renderCellContent?: (cell: Cell<DataGridFeatures, TData, unknown>) => ReactNode
  /** Ours: replaces the header cell content in every head renderer, e.g. with a sort menu. */
  renderHeadCellContent?: (header: Header<DataGridFeatures, TData, unknown>) => ReactNode
  /** Label of the onRowCreate affordance. Defaults to "Add row". */
  rowCreateLabel?: ReactNode
  /**
   * Ours: the side each sticky column sticks to, by column id. A pinned column left out scrolls
   * with the rest; without this, every pinned column sticks. A plain object, so the context
   * compares it by value and republishes when it changes.
   */
  stickySides?: Record<string, 'end' | 'start'>
  table?: Table<TFeatures, TData>
  tableClassNames?: {
    base?: string
    body?: string
    /** Ours: every body cell, skeleton cells included, after the grid's own classes. */
    bodyCell?: string
    bodyRow?: string
    /** The fill handle, e.g. to restyle or resize it beyond the built-in variants. */
    cellFillHandle?: string
    edgeCell?: string
    footer?: string
    /** Ours: every head cell, after the grid's own classes. */
    headCell?: string
    header?: string
    headerRow?: string
    headerSticky?: string
    /**
     * Ours: every column resize handle, after the grid's own classes. The handle draws its line
     * with `before:`, and carries `data-resizing` while it's dragged.
     */
    resizeHandle?: string
    /** The onRowCreate affordance row, e.g. to match the grid's row height. */
    rowCreate?: string
    rowDeleted?: string
    rowDirty?: string
    /** Override the getRowStatus tints; defaults are the warning-muted set. */
    rowNew?: string
    /**
     * Override the pinned-row chrome (muted tint, boundary shadow). A grid
     * that pins a draft row for quick-create wants it to read as an active
     * row, not a muted one.
     */
    rowPinned?: string
  }
  tableLayout?: {
    cellBorder?: boolean
    /**
     * When true, committing an edit with Enter moves focus down (Shift+Enter
     * up), the Sheets and Excel convention. Off by default: the commit keeps
     * focus on the edited cell. Tab always commits and moves across.
     */
    cellEditEnterAdvance?: boolean
    /**
     * How a cell opens its editor from the mouse: "dblclick" (default,
     * the spreadsheet standard - first click focuses, second edits) or
     * "click", opening the editor as soon as a plain click lands on the
     * focused cell; modifier clicks and drags still select.
     */
    cellEditMode?: 'click' | 'dblclick'
    /** Drag-to-fill handle on the selection corner. Requires cellSelection. */
    cellFillHandle?: boolean
    /**
     * The handle's look: "dot" is a solid primary dot kept inside the
     * cell corner; "ring" is the Sheets-style hollow circle riding the
     * corner point, clamped at the grid's own boundary; "square" is the
     * Excel-style solid square inside the corner. Default "dot".
     */
    cellFillHandleVariant?: 'dot' | 'ring' | 'square'
    /** Spreadsheet cell range selection: mouse ranges, keyboard, clipboard chrome. */
    cellSelection?: boolean
    /**
     * "range" (default): drag, Shift and Ctrl/Cmd gestures and Shift+keys
     * grow multi-cell regions. "single": one focused cell only - every
     * gesture collapses to it and Ctrl/Cmd+A is inert.
     */
    cellSelectionMode?: 'range' | 'single'
    columnsDraggable?: boolean
    columnsMovable?: boolean
    columnsPinnable?: boolean
    columnsResizable?: boolean
    columnsVisibility?: boolean
    dense?: boolean
    footerBackground?: boolean
    headerBackground?: boolean
    headerBorder?: boolean
    headerSticky?: boolean
    rowBorder?: boolean
    rowRounded?: boolean
    rowsDraggable?: boolean
    rowsPinnable?: boolean
    stripped?: boolean
    width?: 'auto' | 'fixed'
  }
}

export type DataGridRequestParams = {
  columnFilters?: ColumnFiltersState
  pageIndex: number
  pageSize: number
  sorting?: SortingState
}

/** Ours: what `getRowProps` and `DataGridTableBodyRow` accept for one row. */
export type DataGridRowProps = {
  className?: string
  /** Paints the selected-row background without selecting the row. */
  highlighted?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

/**
 * Lifecycle of a row for the optional CRUD indications: "new" and "dirty"
 * tint the row, "deleted" mutes and strikes it. Purely presentational - the
 * consumer's state stays the only owner of what the statuses mean.
 */
export type DataGridRowStatus = 'deleted' | 'dirty' | 'new'

function createDataGridAutoSizeController<TData extends object>(
  /**
   * A getter, not the table itself.
   *
   * v8 handed back one stable table whose state mutated in place, so a
   * controller could close over it. v9 returns a NEW table wrapper on every
   * state change, and a captured one keeps reporting the state it was built
   * with - here that meant `columnSizing` looked permanently empty, the
   * applied-once guard re-armed on every measurement, and the fill overwrote
   * whatever width the user had just dragged the column to.
   */
  getTable: () => DataGridTableInstance<TData>,
): DataGridAutoSizeController {
  // Reflow coalescing: a live window drag streams a measurement per frame,
  // and every accepted commit re-renders the table. One leading commit
  // keeps single snaps (a maximize) instant; the rest of the stream parks
  // its latest value behind one trailing timer, so a continuous drag costs
  // two commits instead of sixty a second. The trailing timer may fire
  // after the grid unmounts; it re-reads the table then, and a sizing
  // write to the consumer's store is inert once nothing renders it.
  const SETTLE_MS = 150

  // The armed column's reflow target at this free space. Shared by reflow,
  // which commits it, and preview, which only reports it.
  const resolveReflow = (freeSpace: number) => {
    const table = getTable()
    const state = getDataGridAutoSizeState(table.store)
    // LIVE state, never the render snapshot: a measurement can run between
    // our own sizing commit and React's re-render, and the stale snapshot
    // would hide the width this controller just wrote.
    const columnSizing = table.atoms.columnSizing?.get() ?? table.state.columnSizing
    if (!state.applied) return null
    const autoSizeColumn = table
      .getVisibleLeafColumns()
      .find(column => column.columnDef.meta?.autoSize && column.getCanResize())
    if (!autoSizeColumn || autoSizeColumn.id !== state.applied.columnId) return null
    // A width this coordinator did not write belongs to the user's drag;
    // reflow stands down until a reset re-arms the column.
    const currentSize = columnSizing[state.applied.columnId]
    if (currentSize !== undefined && currentSize !== state.applied.grown) {
      return null
    }
    // The free space as it would measure with our growth removed; the
    // target re-absorbs exactly that, floored at minSize so a narrow
    // window hands space back without ever crushing the column (no
    // explicit minSize floors at the starting width instead). The resolved
    // columnDef always carries TanStack's built-in minSize (20), so only a
    // value that differs from the defaults, or a defaultColumn.minSize,
    // counts as one the consumer set.
    const freeAtBase = freeSpace + (state.applied.grown - state.applied.base)
    const resolvedMinSize = autoSizeColumn.columnDef.minSize
    const hasConsumerMinSize =
      table.options.defaultColumn?.minSize !== undefined ||
      resolvedMinSize !== table.getDefaultColumnDef().minSize
    const floor =
      hasConsumerMinSize && resolvedMinSize !== undefined ? resolvedMinSize : state.applied.base
    const target = Math.max(floor, state.applied.base + freeAtBase)
    return {
      applied: state.applied,
      // Always set on a resolved columnDef (MAX_SAFE_INTEGER by default).
      maxSize: autoSizeColumn.columnDef.maxSize!,
      state,
      table,
      target,
    }
  }

  const reflow = (freeSpace: number): boolean => {
    const resolved = resolveReflow(freeSpace)
    if (!resolved) return false
    const { applied, state, table, target } = resolved
    if (Math.abs(target - applied.grown) < 1) return false
    state.applied = { ...applied, grown: target }
    table.setColumnSizing(old => ({
      ...old,
      [applied.columnId]: target,
    }))
    return true
  }

  return {
    adopt(columnId: string, size: number) {
      const state = getDataGridAutoSizeState(getTable().store)
      if (state.applied?.columnId === columnId) {
        state.applied = { ...state.applied, grown: size }
      }
    },
    apply(freeSpace: number) {
      const table = getTable()
      const state = getDataGridAutoSizeState(table.store)
      // Same live read as `reflow`; the snapshot race is what made the
      // re-arm check clear the bookkeeping right after the first absorb.
      const columnSizing = table.atoms.columnSizing?.get() ?? table.state.columnSizing

      // Re-arm after reset flows (double-click resetSize, resetColumnSizing,
      // controlled state replacement) so the column re-fills instead of
      // leaving a dead blank strip.
      if (state.applied && columnSizing[state.applied.columnId] === undefined) {
        state.applied = null
      }

      const autoSizeColumn = table
        .getVisibleLeafColumns()
        .find(column => column.columnDef.meta?.autoSize && column.getCanResize())

      if (autoSizeColumn && state.applied?.columnId === autoSizeColumn.id) {
        state.pendingFreeSpace = freeSpace
        const now = Date.now()
        if (now - state.lastCommitAt > SETTLE_MS) {
          state.lastCommitAt = now
          return reflow(freeSpace)
        }
        if (state.settleTimer !== null) clearTimeout(state.settleTimer)
        state.settleTimer = setTimeout(() => {
          state.settleTimer = null
          state.lastCommitAt = Date.now()
          reflow(state.pendingFreeSpace)
        }, SETTLE_MS)
        return false
      }

      const fillWidth = Math.max(0, freeSpace)
      if (fillWidth <= 0) return false

      if (!autoSizeColumn) {
        return false
      }

      // A width this coordinator did not write belongs to someone else -
      // almost always the user, who just dragged the column's resize handle.
      // Filling over it is what made a `meta.autoSize` column look
      // un-resizable: the drag committed, the next viewport measurement
      // stamped the fill back on top, and the column snapped to its old width.
      //
      // Deliberately keyed on observed state rather than on `state.applied`, which
      // is per-coordinator memory: anything that rebuilds the coordinator
      // (a remount, a new table store) forgets what it did, and the guard has
      // to survive that. An explicit reset clears the entry and re-arms the
      // fill, which is what makes double-click-to-reset still work.
      const currentSize = columnSizing[autoSizeColumn.id]
      if (currentSize !== undefined && currentSize !== state.applied?.grown) {
        return false
      }

      // Candidate switched (e.g. the grown column was hidden and another
      // meta.autoSize column took over): revert the previous growth if the
      // user hasn't manually resized that column since, so visibility
      // toggles cannot ratchet the table wider than its container forever.
      const revert =
        state.applied && columnSizing[state.applied.columnId] === state.applied.grown
          ? state.applied
          : null
      const base = columnSizing[autoSizeColumn.id] ?? autoSizeColumn.getSize()
      const grown = base + fillWidth

      state.applied = { base, columnId: autoSizeColumn.id, grown }
      table.setColumnSizing(old => {
        const next = { ...old, [autoSizeColumn.id]: grown }
        if (revert && next[revert.columnId] === revert.grown) {
          next[revert.columnId] = revert.base
        }
        return next
      })

      return true
    },
    preview(freeSpace: number) {
      const resolved = resolveReflow(freeSpace)
      // In rendered pixels: getSize() clamps to maxSize, so an unclamped
      // preview painted a capped column past its cap until the release.
      return resolved
        ? {
            columnId: resolved.applied.columnId,
            current: Math.min(resolved.applied.grown, resolved.maxSize),
            size: Math.min(resolved.target, resolved.maxSize),
          }
        : null
    },
  }
}

function getDataGridAutoSizeState(store: object): DataGridAutoSizeReflowState {
  let state = dataGridAutoSizeStates.get(store)
  if (!state) {
    state = {
      applied: null,
      lastCommitAt: 0,
      pendingFreeSpace: 0,
      settleTimer: null,
    }
    dataGridAutoSizeStates.set(store, state)
  }
  return state
}

const DataGridContext = createContext<
  // oxlint-disable-next-line typescript/no-explicit-any -- one context serves grids of every row type
  DataGridContextProps<any> | undefined
>(undefined)

type DataGridStateAtom<TValue> = {
  get: () => TValue
  subscribe: (listener: () => void) => { unsubscribe: () => void }
}

/**
 * Reads the grid context. Pass `TData` from the calling component when the
 * table, a row or a cell is handed on to something typed against that row
 * shape: v9 declares `TData` invariant, so the default `any` no longer
 * unifies with a concrete row type the way it did on v8.
 */
function useDataGrid<
  // oxlint-disable-next-line typescript/no-explicit-any -- callers that hand on rows pass TData; see above
  TData extends object = any,
>(): DataGridContextProps<TData> {
  const context = useContext(DataGridContext) as DataGridContextProps<TData> | undefined
  if (!context) {
    throw new Error('useDataGrid must be used within a DataGridProvider')
  }
  return context
}

const noopUnsubscribe = () => {}

/**
 * Re-renders the caller when one table state atom changes, without waiting
 * for a render from above. The context holds still while a column resize is
 * in flight (see DataGridProvider), and React Compiler caches a consumer's
 * `<DataGridTable />` element, so whatever a drag must keep live (the width
 * variables, the resize line and indicator) subscribes here. `select` must
 * return the slice itself or a primitive, or every write re-renders.
 */
function useDataGridAtom<TValue, TSelected = TValue | undefined>(
  atom: DataGridStateAtom<TValue> | undefined,
  select: (value: TValue | undefined) => TSelected = value => value as TSelected,
): TSelected {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!atom) return noopUnsubscribe
      const subscription = atom.subscribe(onChange)
      return () => subscription.unsubscribe()
    },
    [atom],
  )
  const getSnapshot = () => select(atom?.get())
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

// Slices kept out of the inputs: cells paint cell selection through their
// own Subscribe, and a resize drag paints through useDataGridAtom.
const DATA_GRID_UNPUBLISHED_STATE = new Set(['cellSelection', 'columnResizing'])

const isDataGridPropNode = (value: unknown) =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('$$typeof' in value || Object.getPrototypeOf(value) !== Object.prototype)

// Functions and React nodes count by presence here; their identity is a
// callback input.
function dataGridPropsSignatureReplacer(_key: string, value: unknown) {
  if (typeof value === 'function') return 'fn'
  if (isDataGridPropNode(value)) return 'node'
  return value
}

// Every callback and React node prop, compared by identity.
function getDataGridCallbackInputs<TData extends object>(
  props: DataGridLayoutProps<TData>,
): unknown[] {
  return Object.values(props).filter(
    value => typeof value === 'function' || isDataGridPropNode(value),
  )
}

// Inputs that always publish: every state slice but the two above, `data`
// and `columns` (TanStack needs both stable already), and props by value.
function getDataGridStateInputs<TData extends object>(
  table: DataGridTableInstance<TData>,
  props: DataGridLayoutProps<TData>,
  autoSize: DataGridAutoSizeController,
): unknown[] {
  const inputs: unknown[] = [
    autoSize,
    table.options.data,
    table.options.columns,
    JSON.stringify(props, dataGridPropsSignatureReplacer),
  ]
  for (const [key, slice] of Object.entries(table.state)) {
    if (!DATA_GRID_UNPUBLISHED_STATE.has(key)) inputs.push(key, slice)
  }
  return inputs
}

const isSameDataGridInputs = (previous: unknown[], next: unknown[]) =>
  previous.length === next.length && next.every((input, index) => Object.is(input, previous[index]))

function DataGrid<TFeatures extends TableFeatures, TData extends object>({
  children,
  table,
  ...props
}: DataGridProps<TFeatures, TData>) {
  const defaultProps: Partial<DataGridProps<TFeatures, TData>> = {
    loadingMode: 'skeleton',
    tableClassNames: {
      base: '',
      body: '',
      bodyRow: '',
      edgeCell: '',
      footer: '',
      header: '',
      headerRow: '',
      // z-40 keeps the sticky header above pinned body cells (zIndex 30 in
      // getPinningStyles), which would otherwise paint over it while
      // scrolling vertically with columnsPinnable enabled.
      headerSticky: 'sticky top-0 z-40 bg-background/90 backdrop-blur-xs',
    },
    tableLayout: {
      cellBorder: false,
      cellFillHandle: false,
      cellSelection: false,
      columnsDraggable: false,
      columnsMovable: false,
      columnsPinnable: false,
      columnsResizable: false,
      columnsVisibility: false,
      dense: false,
      footerBackground: false,
      headerBackground: false,
      headerBorder: true,
      headerSticky: false,
      rowBorder: true,
      rowRounded: false,
      rowsDraggable: false,
      rowsPinnable: false,
      stripped: false,
      width: 'fixed',
    },
  }

  const mergedProps: DataGridProps<TFeatures, TData> = {
    ...defaultProps,
    ...props,
    tableClassNames: {
      ...defaultProps.tableClassNames,
      ...(props.tableClassNames || {}),
    },
    tableLayout: {
      ...defaultProps.tableLayout,
      ...(props.tableLayout || {}),
    },
  }

  // Ensure table is provided
  if (!table) {
    throw new Error('DataGrid requires a "table" prop')
  }

  // The single widening point. Consumers own the TanStack core and may hand
  // over any feature bundle; internals need a concrete one to resolve the
  // feature-gated APIs they call, and v9's invariant TFeatures rules out
  // expressing that with a generic constraint.
  const internalTable = table as unknown as DataGridTableInstance<TData>
  const internalProps = mergedProps as unknown as DataGridLayoutProps<TData>

  return (
    <DataGridProvider table={internalTable} {...internalProps} data-sentry-element="DataGridProvider" data-sentry-component="DataGrid" data-sentry-source-file="data-grid.tsx">
      {children}
    </DataGridProvider>
  )
}

function DataGridContainer({
  children,
  className,
}: {
  /** Accepted for backwards compatibility; currently has no effect. */
  border?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div
      // relative: anchors floating chrome composed inside the container,
      // like the fill-drag preview outline.
      className={cn('relative w-full overflow-hidden', className)}
      data-slot="data-grid"
    data-sentry-component="DataGridContainer" data-sentry-source-file="data-grid.tsx">
      {children}
    </div>
  )
}

function DataGridProvider<TData extends object>({
  children,
  table,
  ...props
}: DataGridLayoutProps<TData> & {
  children?: ReactNode
  table: DataGridTableInstance<TData>
}) {
  // Stable for the grid's lifetime; ARIA ids and relations hang off it.
  const gridId = useId()
  // Latest-value refs behind the context getters: a reader always gets the
  // current props, labels and table, while the published value changes only
  // when its inputs do (below). v9 re-creates the table on every state
  // change, including each resize tick.
  const propsRef = useRef(props)
  propsRef.current = props
  const i18n = mergeDataGridI18n(props.i18n)
  const i18nRef = useRef(i18n)
  i18nRef.current = i18n
  const tableRef = useRef(table)
  tableRef.current = table

  const cellSelectionOn = !!props.tableLayout?.cellSelection

  // Rendered row indexes re-base per page, so a range kept across a page flip
  // re-lights the same positions over different rows. A page change clears
  // the selection instead - the rule Excel-family grids apply to paginated
  // range selection.
  const pageKey = `${table.state.pagination?.pageIndex}:${table.state.pagination?.pageSize}`
  const previousPageKeyRef = useRef(pageKey)
  useEffect(() => {
    if (previousPageKeyRef.current === pageKey) return
    previousPageKeyRef.current = pageKey
    if (!cellSelectionOn || table.atoms.cellSelection == null) return
    tableRef.current.resetCellSelection(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, cellSelectionOn])

  // One autoSize coordinator per table instance so split header/body viewports
  // cannot apply the growth twice. Keyed on `table.store`, which v9 keeps
  // stable for the life of the table, rather than on `table` itself: the
  // wrapper is re-created on every state change, and re-creating the
  // controller with it would reset its applied-once bookkeeping mid-drag.
  const autoSize = useMemo(
    () => createDataGridAutoSizeController(() => tableRef.current),
    [table.store],
  )

  // A new context value re-renders every useDataGrid reader, every cell
  // included, past any memo, and it is the only update a part gets when React
  // Compiler cached the element above it. The grid publishes one when a state
  // input changes. Callback inputs publish only for a consumer that memoizes
  // its useTable options, as the compiler does: its callbacks then change
  // only when they really change. A consumer that is not compiled builds new
  // options and new inline callbacks on every render, re-renders the grid
  // from above anyway, and would otherwise re-render every memoized virtual
  // row. Not the children's identity: the compiler caches the elements
  // inside children separately, so fresh children can wrap a cached table.
  // While a column resize is in flight the value holds still: the drag's own
  // visuals subscribe through useDataGridAtom, and the render after it
  // publishes what it changed.
  const committedOptionsRef = useRef(table.options)
  const hasMemoizedOptions = committedOptionsRef.current === table.options
  useLayoutEffect(() => {
    committedOptionsRef.current = table.options
  })
  const isResizing = !!table.state.columnResizing?.isResizingColumn
  const publishedRef = useRef<null | {
    callbackInputs: unknown[]
    stateInputs: unknown[]
    value: DataGridContextProps<TData>
  }>(null)
  let published = publishedRef.current
  if (!published || !isResizing) {
    const stateInputs = getDataGridStateInputs(table, props, autoSize)
    const callbackInputs = getDataGridCallbackInputs(props)
    const stateChanged = !published || !isSameDataGridInputs(published.stateInputs, stateInputs)
    const callbacksChanged =
      !published || !isSameDataGridInputs(published.callbackInputs, callbackInputs)
    if (published && !stateChanged && callbacksChanged && !hasMemoizedOptions) {
      published.callbackInputs = callbackInputs
    } else if (stateChanged || callbacksChanged) {
      published = {
        callbackInputs,
        stateInputs,
        value: {
          autoSize,
          gridId,
          get i18n() {
            return i18nRef.current
          },
          isLoading: props.isLoading || false,
          get props() {
            return propsRef.current
          },
          recordCount: props.recordCount,
          get table() {
            return tableRef.current
          },
        },
      }
      publishedRef.current = published
    }
  }
  const value = published!.value

  return (
    // One React context serves every TData, but v9 declares both TFeatures and
    // TData invariant, so a `DataGridContextProps<any>` context cannot accept a
    // `DataGridContextProps<TData>` value structurally. The erasure happens
    // here and is undone by the TData generic on each consumer component.
    <DataGridContext.Provider value={value as unknown as DataGridContextProps<TData>} data-sentry-element="DataGridContext.Provider" data-sentry-component="DataGridProvider" data-sentry-source-file="data-grid.tsx">
      {children}
    </DataGridContext.Provider>
  )
}

export { DataGrid, DataGridContainer, DataGridProvider, useDataGrid, useDataGridAtom }
